/**
 * URL secret redaction, mirrors the v2 app helper so both apps scrub the same keys.
 * Used by the Segment source middleware and Sentry beforeSend/beforeBreadcrumb.
 */

/** Matches query/fragment param *keys* that carry secrets. */
export const SECRET_URL_PARAM_KEY_PATTERN = /token|secret|password/i

export const REDACTED_URL_PARAM_VALUE = '[REDACTED]'

/**
 * Keys redacted only on exact match (too generic for a substring rule).
 * `code` is the OAuth authorization code from the Google SSO callback.
 * These are redacted in tracker payloads only; route code does not
 * strip them from the live URL.
 */
const EXACT_SECRET_URL_PARAM_KEYS = ['code']
const EXACT_KEYS_ALTERNATION = EXACT_SECRET_URL_PARAM_KEYS.join('|')

/**
 * Matches `?key=value`, `&key=value` and `#key=value` pairs whose key contains
 * token/secret/password, or equals one of the exact keys. Group 1 is the
 * separator, group 2 the key.
 */
const SECRET_URL_PARAM_PAIR_PATTERN = new RegExp(
  `([?&#])([^=&#]*(?:token|secret|password)[^=&#]*|${EXACT_KEYS_ALTERNATION})=[^&#]*`,
  'gi'
)

/**
 * Same, but for pairs nested percent-encoded inside another param value, e.g.
 * `redirect=%2Fapply%3Ftoken%3Dabc%26x%3D1`. Separators are `%3F`/`%26`/`%23`,
 * the key/value delimiter is `%3D`, and the value ends at the next raw `&`/`#`
 * or encoded `%26`/`%23`.
 */
const ENCODED_SECRET_URL_PARAM_PAIR_PATTERN = new RegExp(
  `(%3F|%26|%23)((?:(?!%3D)[^&#])*?(?:token|secret|password)(?:(?!%3D)[^&#])*?|${EXACT_KEYS_ALTERNATION})%3D((?:(?!%26|%23)[^&#])*)`,
  'gi'
)

export function isSecretUrlParamKey (key) {
  return SECRET_URL_PARAM_KEY_PATTERN.test(key)
}

/**
 * Replaces the value of every secret-looking param in a URL, path+query or
 * query/fragment string with `[REDACTED]`. Also catches pairs that are
 * percent-encoded inside another param value (e.g. a `redirect` url).
 * Non-string input passes through.
 */
export function redactSecretUrlParams (value) {
  if (typeof value !== 'string') {
    return value
  }
  return value
    .replace(SECRET_URL_PARAM_PAIR_PATTERN, `$1$2=${REDACTED_URL_PARAM_VALUE}`)
    .replace(ENCODED_SECRET_URL_PARAM_PAIR_PATTERN, `$1$2%3D${REDACTED_URL_PARAM_VALUE}`)
}

/** Redacts the given string keys of `obj` in place. Missing/non-object `obj` is a no-op. */
export function redactSecretUrlParamsInKeys (obj, keys) {
  if (!obj) return
  keys.forEach((key) => { obj[key] = redactSecretUrlParams(obj[key]) })
}
